import React, { useMemo, useState } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ReplyRoundedIcon from '@mui/icons-material/ReplyRounded';
import CustomButton from '../v2/general/CustomButton/CustomButton';

export interface StatusDashboardAlert {
  id: string;
  title: string;
  timestamp: string;
}

export interface StatusDashboardAction {
  id: string;
  title: string;
  timestamp: string;
  subtitle?: string;
  unread?: boolean;
}

export interface StatusDashboardData {
  services: {
    total: number;
    escalados: number;
    subcontracted: number;
    porEscalar: number;
    missingDriver: number;
    missingVehicle: number;
  };
  coverage: {
    total: number;
    covered: number;
    incomplete: number;
  };
  drivers: {
    total: number;
    escalados: number;
    naoEscalados: number;
    fora: number;
    alerts: Array<{ id: string; label: string; count: number }>;
  };
  vehicles: {
    total: number;
    escalados: number;
    naoEscalados: number;
    emOficina: number;
    alerts: Array<{ id: string; label: string; count: number }>;
  };
  alerts: {
    unread: StatusDashboardAlert[];
    read: StatusDashboardAlert[];
  };
  lastActions?: StatusDashboardAction[];
}

type DonutGradientKey = 'green' | 'orange' | 'blue' | 'gray' | 'amber';

interface DonutSegment {
  value: number;
  colorStart: string;
  colorEnd: string;
  gradientKey?: DonutGradientKey;
}

const donutGradients = {
  blue: { start: '#00A5FF', end: '#37CDFF' },
  green: { start: '#16C53D', end: '#00A5FF' },
  orange: { start: '#FF5C00', end: '#EDBF67' },
  gray: { start: '#8A97B2', end: '#C7D1E0' },
  amber: { start: '#F5A623', end: '#F8C56B' },
};

const polarToCartesian = (
  centerX: number,
  centerY: number,
  radius: number,
  angleInDegrees: number,
) => {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180;
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
};

const describeArc = (
  centerX: number,
  centerY: number,
  radius: number,
  startAngle: number,
  endAngle: number,
) => {
  const start = polarToCartesian(centerX, centerY, radius, endAngle);
  const end = polarToCartesian(centerX, centerY, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

  return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
};

const getDonutGradientCoords = (
  size: number,
  key?: DonutGradientKey,
) => {
  if (key === 'orange') {
    return {
      x1: size * 0.18896,
      y1: size * 0,
      x2: size * 0.17527,
      y2: size * 1.84602,
    };
  }

  return {
    x1: size * 1.75293,
    y1: size * -0.77977,
    x2: size * 2.04152,
    y2: size * 1.17267,
  };
};

const DonutChart = ({
  value,
  total,
  segments,
  size = 96,
  thickness = 12,
  centerContent,
}: {
  value: number;
  total: number;
  segments: DonutSegment[];
  size?: number;
  thickness?: number;
  centerContent?: React.ReactNode;
}) => {
  const resolvedThickness = thickness ?? Math.round(size * 0.2);
  const innerSize = size - resolvedThickness * 2;
  const radius = (size - resolvedThickness) / 2;
  const center = size / 2;
  const idPrefix = React.useId();
  const totalValue = segments.reduce((sum, segment) => sum + segment.value, 0) || 1;
  const capAngle =
    radius > 0
      ? ((resolvedThickness / 2) / radius) * (180 / Math.PI)
      : 0;

  let cumulativeAngle = 0;
  const arcSegments = segments
    .filter((segment) => segment.value > 0)
    .map((segment, index, activeSegments) => {
      const rawAngle = (segment.value / totalValue) * 360;
      let startAngle = cumulativeAngle;
      let endAngle = cumulativeAngle + rawAngle;

      if (activeSegments.length > 1 && capAngle > 0) {
        if (rawAngle <= capAngle * 2) {
          const midAngle = cumulativeAngle + rawAngle / 2;
          startAngle = midAngle - capAngle / 2;
          endAngle = midAngle + capAngle / 2;
        } else {
          startAngle = cumulativeAngle + capAngle;
          endAngle = cumulativeAngle + rawAngle - capAngle;
        }
      }

      cumulativeAngle += rawAngle;
      if (endAngle <= startAngle) {
        return null;
      }

      const gradientId = `${idPrefix}-seg-${index}`;
      return {
        path: describeArc(center, center, radius, startAngle, endAngle),
        gradientId,
        gradientCoords: getDonutGradientCoords(size, segment.gradientKey),
        colors: {
          start: segment.colorStart,
          end: segment.colorEnd,
        },
      };
    })
    .filter(
      (segment): segment is NonNullable<typeof segment> => segment !== null,
    );

  const defaultCenter = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '2px',
      }}
    >
      <Typography
        sx={{
          fontFamily: 'Poppins',
          fontWeight: 700,
          fontSize: '20px',
          color: '#092056',
          lineHeight: 1,
        }}
      >
        {value}
      </Typography>
      <Typography
        sx={{
          fontFamily: 'Poppins',
          fontWeight: 500,
          fontSize: '11px',
          color: '#8695AF',
          lineHeight: 1,
        }}
      >
        /{total}
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
      }}
    >
      <Box
        component='svg'
        sx={{
          position: 'absolute',
          inset: 0,
        }}
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
      >
        <defs>
          {arcSegments.map((segment) => (
            <linearGradient
              key={segment.gradientId}
              id={segment.gradientId}
              x1={segment.gradientCoords.x1}
              y1={segment.gradientCoords.y1}
              x2={segment.gradientCoords.x2}
              y2={segment.gradientCoords.y2}
              gradientUnits='userSpaceOnUse'
            >
              <stop stopColor={segment.colors.start} />
              <stop offset='1' stopColor={segment.colors.end} />
            </linearGradient>
          ))}
        </defs>
        <circle
          cx={center}
          cy={center}
          r={radius}
          stroke='#E6ECF5'
          strokeWidth={resolvedThickness}
          fill='none'
        />
        {arcSegments.map((segment) => (
          <path
            key={segment.gradientId}
            d={segment.path}
            stroke={`url(#${segment.gradientId})`}
            strokeWidth={resolvedThickness}
            strokeLinecap='round'
            fill='none'
          />
        ))}
      </Box>
      <Box
        sx={{
          width: innerSize,
          height: innerSize,
          borderRadius: '50%',
          backgroundColor: '#FFFFFF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {centerContent ?? defaultCenter}
      </Box>
    </Box>
  );
};

const EscalonamentoLegendItem = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactElement;
  label: string;
  value: number;
}) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: '0px',
      alignItems: 'flex-start',
      whiteSpace: 'nowrap',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    <Box
      sx={{
        width: '26px',
        height: '26px',
        borderRadius: '13px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {icon}
    </Box>
      <Typography
        sx={{
          fontFamily: 'Poppins',
          fontWeight: 600,
          fontSize: '16px',
          lineHeight: 1.75,
          letterSpacing: '0.15px',
          color: '#092056',
        }}
      >
        {value}
      </Typography>
    </Box>
    <Typography
      sx={{
        fontFamily: 'Poppins',
        fontWeight: 400,
        fontSize: '12px',
        lineHeight: 1.3,
        color: '#092056',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Typography>
  </Box>
);

const legendCheckIconSrc =
  'http://localhost:3845/assets/c598756a5216e926f220711910dcf0d399c8fe71.svg';
const legendCancelIconSrc =
  'http://localhost:3845/assets/ae3a04eed20eca44ba8c0b8f67938cc9fa33951e.svg';

const LegendIcon = ({
  iconSrc,
  background,
  size = 26,
  radius = 13,
  padding = 6.333,
  iconTranslate,
}: {
  iconSrc: string;
  background: Record<string, string>;
  size?: number;
  radius?: number;
  padding?: number;
  iconTranslate?: { x: number; y: number };
}) => (
  <Box
    sx={{
      width: `${size}px`,
      height: `${size}px`,
      borderRadius: `${radius}px`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: `${padding}px`,
      ...background,
    }}
  >
    <Box
      component='img'
      src={iconSrc}
      alt=''
      sx={{
        width: '13.333px',
        height: '13.333px',
        display: 'block',
        transform: iconTranslate
          ? `translate(${iconTranslate.x}px, ${iconTranslate.y}px)`
          : undefined,
      }}
    />
  </Box>
);

const buildDonutCenter = (value: number, total: number) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
    <Typography
      sx={{
        fontFamily: 'Poppins',
        fontWeight: 600,
        fontSize: '24px',
        lineHeight: 1.334,
        color: '#092056',
      }}
    >
      {value}
    </Typography>
    <Typography
      sx={{
        fontFamily: 'Poppins',
        fontWeight: 500,
        fontSize: '13px',
        lineHeight: 1.3,
        color: '#8695AF',
      }}
    >
      /{total}
    </Typography>
  </Box>
);

const buildCompactDonutCenter = (value: number, total: number) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
    <Typography
      sx={{
        fontFamily: 'Poppins',
        fontWeight: 600,
        fontSize: '20px',
        lineHeight: 1.334,
        color: '#092056',
      }}
    >
      {value}
    </Typography>
    <Typography
      sx={{
        fontFamily: 'Poppins',
        fontWeight: 500,
        fontSize: '11px',
        lineHeight: 1.3,
        color: '#8695AF',
      }}
    >
      /{total}
    </Typography>
  </Box>
);

interface SchedulesStatusDashboardProps {
  data: StatusDashboardData;
}

const SchedulesStatusDashboard = React.memo(function SchedulesStatusDashboard({
  data,
}: SchedulesStatusDashboardProps) {
  const [alertsTab, setAlertsTab] = useState(0);

  const serviceStats = data.services;
  const coverageStats = data.coverage;
  const driverStats = data.drivers;
  const vehicleStats = data.vehicles;

  const scheduledServicesCount = serviceStats.escalados + serviceStats.subcontracted;
  const servicesSegments: DonutSegment[] = useMemo(
    () => [
      {
        value: serviceStats.escalados,
        colorStart: donutGradients.green.start,
        colorEnd: donutGradients.green.end,
        gradientKey: 'green',
      },
      {
        value: serviceStats.subcontracted,
        colorStart: donutGradients.gray.start,
        colorEnd: donutGradients.gray.end,
        gradientKey: 'gray',
      },
      {
        value: serviceStats.porEscalar,
        colorStart: donutGradients.orange.start,
        colorEnd: donutGradients.orange.end,
        gradientKey: 'orange',
      },
    ],
    [serviceStats],
  );
  const escalonamentoCenter = buildDonutCenter(
    scheduledServicesCount,
    serviceStats.total,
  );
  const escalonamentoDonutSize = 145.466;
  const escalonamentoDonutThickness = 17;
  const standardDonutThickness = 17;

  const coverageSegments: DonutSegment[] = useMemo(
    () => [
      {
        value: coverageStats.covered,
        colorStart: donutGradients.green.start,
        colorEnd: donutGradients.green.end,
        gradientKey: 'green',
      },
      {
        value: coverageStats.incomplete,
        colorStart: donutGradients.orange.start,
        colorEnd: donutGradients.orange.end,
        gradientKey: 'orange',
      },
    ],
    [coverageStats],
  );

  const coverageCenter = buildDonutCenter(
    coverageStats.covered,
    coverageStats.total,
  );

  const driversSegments: DonutSegment[] = useMemo(
    () => [
      {
        value: driverStats.escalados,
        colorStart: donutGradients.green.start,
        colorEnd: donutGradients.green.end,
        gradientKey: 'green',
      },
      {
        value: driverStats.naoEscalados,
        colorStart: donutGradients.orange.start,
        colorEnd: donutGradients.orange.end,
        gradientKey: 'orange',
      },
      {
        value: driverStats.fora,
        colorStart: donutGradients.gray.start,
        colorEnd: donutGradients.gray.end,
        gradientKey: 'gray',
      },
    ],
    [driverStats],
  );

  const vehiclesSegments: DonutSegment[] = useMemo(
    () => [
      {
        value: vehicleStats.escalados,
        colorStart: donutGradients.blue.start,
        colorEnd: donutGradients.blue.end,
        gradientKey: 'blue',
      },
      {
        value: vehicleStats.naoEscalados,
        colorStart: donutGradients.orange.start,
        colorEnd: donutGradients.orange.end,
        gradientKey: 'orange',
      },
      {
        value: vehicleStats.emOficina,
        colorStart: donutGradients.gray.start,
        colorEnd: donutGradients.gray.end,
        gradientKey: 'gray',
      },
    ],
    [vehicleStats],
  );

  const renderAlertTitle = (title: string) =>
    title.split(/(\d{3,})/g).map((part, index) => {
      const isNumberToken = /^\d{3,}$/.test(part);
      return (
        <Typography
          key={`${title}-${index}`}
          component='span'
          sx={{
            fontWeight: 'inherit',
            fontSize: 'inherit',
            lineHeight: 'inherit',
            ...(isNumberToken
              ? { textDecoration: 'underline', textDecorationThickness: '1px' }
              : null),
          }}
        >
          {part}
        </Typography>
      );
    });

  const lastActions = useMemo(() => {
    if (data.lastActions && data.lastActions.length > 0) {
      return data.lastActions.map((action) => ({
        ...action,
        unread: action.unread ?? false,
      }));
    }

    const unreadIds = new Set(data.alerts.unread.map((alert) => alert.id));
    return [...data.alerts.unread, ...data.alerts.read].map((alert) => ({
      id: alert.id,
      title: alert.title,
      timestamp: alert.timestamp,
      unread: unreadIds.has(alert.id),
    }));
  }, [data.alerts.read, data.alerts.unread, data.lastActions]);

  const servicesAlertText = `Existem ${serviceStats.missingDriver} Serviços sem Motorista e ${serviceStats.missingVehicle} Sem Viatura.`;

  return (
    <Box
      sx={{
        padding: '24px',
        borderRadius: '10px',
        background:
          'linear-gradient(180deg, rgba(0, 165, 255, 0.15) 0%, rgba(134, 149, 175, 0.1) 100%)',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 408px' },
          gap: '24px',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <Box
            sx={{
              backgroundColor: '#FFFFFF',
              borderRadius: '10px',
              padding: '24px 16px',
              boxShadow: '0px 8px 20px rgba(9, 32, 86, 0.08)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '24px',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Poppins',
                  fontWeight: 600,
                  fontSize: '24px',
                  lineHeight: 1.334,
                  color: '#092056',
                }}
              >
                Serviços
              </Typography>
              <Box
                sx={{
                  flex: '1 1 360px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '7px 16px',
                  borderRadius: '4px',
                  backgroundImage:
                    'linear-gradient(98.938deg, #FF5C00 0.42425%, #EDBF67 99.576%)',
                }}
              >
                <WarningAmberRoundedIcon sx={{ color: '#FFFFFF', fontSize: 20 }} />
                <Typography
                  sx={{
                    fontFamily: 'Poppins',
                    fontWeight: 500,
                    fontSize: '14px',
                    lineHeight: 1.5,
                    letterSpacing: '0.15px',
                    color: '#FFFFFF',
                  }}
                >
                  {servicesAlertText}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                gap: '24px',
                alignItems: 'center',
                flexWrap: { xs: 'wrap', lg: 'nowrap' },
                paddingTop: '16px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: '24px',
                  alignItems: 'center',
                  flexWrap: { xs: 'wrap', sm: 'nowrap' },
                  flex: '1 1 360px',
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    width: `${escalonamentoDonutSize}px`,
                    height: `${escalonamentoDonutSize}px`,
                  }}
                >
                  <DonutChart
                    value={scheduledServicesCount}
                    total={serviceStats.total}
                    segments={servicesSegments}
                    size={escalonamentoDonutSize}
                    thickness={escalonamentoDonutThickness}
                    centerContent={escalonamentoCenter}
                  />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Typography
                    sx={{
                      fontFamily: 'Poppins',
                      fontWeight: 600,
                      fontSize: '16px',
                      lineHeight: 1.75,
                      letterSpacing: '0.15px',
                      color: '#092056',
                    }}
                  >
                    Escalonamento
                  </Typography>
                  <Box sx={{ display: 'flex', gap: '24px' }}>
                    <EscalonamentoLegendItem
                      icon={
                        <LegendIcon
                          iconSrc={legendCheckIconSrc}
                          background={{
                            backgroundImage:
                              'linear-gradient(85.5519deg, #16C53D 57.784%, #00A5FF 115.35%)',
                          }}
                        />
                      }
                      value={serviceStats.escalados}
                      label='Escalados'
                    />
                    <EscalonamentoLegendItem
                      icon={
                        <LegendIcon
                          iconSrc={legendCheckIconSrc}
                          background={{ backgroundColor: '#8695AF' }}
                        />
                      }
                      value={serviceStats.subcontracted}
                      label='Subcontratados'
                    />
                    <EscalonamentoLegendItem
                      icon={
                        <LegendIcon
                          iconSrc={legendCancelIconSrc}
                          background={{
                            backgroundImage:
                              'linear-gradient(90.4903deg, #FF5C00 0.42425%, #EDBF67 99.576%)',
                          }}
                          iconTranslate={{ x: -0.5, y: 0 }}
                        />
                      }
                      value={serviceStats.porEscalar}
                      label='Por Escalar'
                    />
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  gap: '24px',
                  alignItems: 'center',
                  flexWrap: { xs: 'wrap', sm: 'nowrap' },
                  flex: '1 1 260px',
                  minWidth: 0,
                }}
              >
                <Box sx={{ position: 'relative', width: 120, height: 120 }}>
                  <DonutChart
                    value={coverageStats.covered}
                    total={coverageStats.total}
                    segments={coverageSegments}
                    size={120}
                    thickness={standardDonutThickness}
                    centerContent={coverageCenter}
                  />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Typography
                    sx={{
                      fontFamily: 'Poppins',
                      fontWeight: 600,
                      fontSize: '16px',
                      lineHeight: 1.75,
                      letterSpacing: '0.15px',
                      color: '#092056',
                    }}
                  >
                    Escalados
                  </Typography>
                  <Box sx={{ display: 'flex', gap: '24px' }}>
                    <EscalonamentoLegendItem
                      icon={
                        <LegendIcon
                          iconSrc={legendCheckIconSrc}
                          background={{
                            backgroundImage:
                              'linear-gradient(85.5519deg, #16C53D 57.784%, #00A5FF 115.35%)',
                          }}
                        />
                      }
                      value={coverageStats.covered}
                      label='Cobertos'
                    />
                    <EscalonamentoLegendItem
                      icon={
                        <LegendIcon
                          iconSrc={legendCancelIconSrc}
                          background={{
                            backgroundImage:
                              'linear-gradient(90.4903deg, #FF5C00 0.42425%, #EDBF67 99.576%)',
                          }}
                          iconTranslate={{ x: -0.5, y: 0 }}
                        />
                      }
                      value={coverageStats.incomplete}
                      label='Incompletos'
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: '24px',
            }}
          >
            <Box
              sx={{
                backgroundColor: '#FFFFFF',
                borderRadius: '10px',
                padding: '24px 16px',
                boxShadow: '0px 8px 20px rgba(9, 32, 86, 0.08)',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Poppins',
                  fontWeight: 600,
                  fontSize: '20px',
                  color: '#092056',
                }}
              >
                Motoristas
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: '16px',
                  alignItems: 'center',
                  flexWrap: { xs: 'wrap', sm: 'nowrap' },
                }}
              >
                <DonutChart
                  value={driverStats.escalados}
                  total={driverStats.total}
                  segments={driversSegments}
                  size={98}
                  thickness={standardDonutThickness}
                  centerContent={buildCompactDonutCenter(
                    driverStats.escalados,
                    driverStats.total,
                  )}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Box sx={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                    <EscalonamentoLegendItem
                      icon={
                        <LegendIcon
                          iconSrc={legendCheckIconSrc}
                          background={{
                            backgroundImage:
                              'linear-gradient(85.5519deg, #16C53D 57.784%, #00A5FF 115.35%)',
                          }}
                        />
                      }
                      value={driverStats.escalados}
                      label='Escalados'
                    />
                    <EscalonamentoLegendItem
                      icon={
                        <LegendIcon
                          iconSrc={legendCancelIconSrc}
                          background={{
                            backgroundImage:
                              'linear-gradient(90.4903deg, #FF5C00 0.42425%, #EDBF67 99.576%)',
                          }}
                          iconTranslate={{ x: -0.5, y: 0 }}
                        />
                      }
                      value={driverStats.naoEscalados}
                      label='Não Escalados'
                    />
                    <EscalonamentoLegendItem
                      icon={
                        <LegendIcon
                          iconSrc={legendCancelIconSrc}
                          background={{ backgroundColor: '#8695AF' }}
                          iconTranslate={{ x: 0.5, y: 0.5 }}
                        />
                      }
                      value={driverStats.fora}
                      label='Fora'
                    />
                  </Box>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {driverStats.alerts.map((item, index) => (
                  <Box
                    key={item.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      height: '48px',
                      borderBottom:
                        index === driverStats.alerts.length - 1
                          ? 'none'
                          : '1px solid rgba(134, 149, 175, 0.15)',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', px: '4px' }}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '100px',
                          backgroundColor: '#FF5C00',
                          color: '#FFFFFF',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontFamily: 'Poppins',
                          fontWeight: 600,
                          fontSize: '16px',
                          lineHeight: 1.75,
                        }}
                      >
                        {item.count}
                      </Box>
                    </Box>
                    <Typography
                      sx={{
                        flex: 1,
                        fontFamily: 'Poppins',
                        fontWeight: 500,
                        fontSize: '13px',
                        color: '#092056',
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <ChevronRightRoundedIcon
                        sx={{ color: '#00A5FF', fontSize: 20 }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>

            <Box
              sx={{
                backgroundColor: '#FFFFFF',
                borderRadius: '10px',
                padding: '24px 16px',
                boxShadow: '0px 8px 20px rgba(9, 32, 86, 0.08)',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Poppins',
                  fontWeight: 600,
                  fontSize: '20px',
                  color: '#092056',
                }}
              >
                Viaturas
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: '16px',
                  alignItems: 'center',
                  flexWrap: { xs: 'wrap', sm: 'nowrap' },
                }}
              >
                <DonutChart
                  value={vehicleStats.escalados}
                  total={vehicleStats.total}
                  segments={vehiclesSegments}
                  size={98}
                  thickness={standardDonutThickness}
                  centerContent={buildCompactDonutCenter(
                    vehicleStats.escalados,
                    vehicleStats.total,
                  )}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Box sx={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                    <EscalonamentoLegendItem
                      icon={
                        <LegendIcon
                          iconSrc={legendCheckIconSrc}
                          background={{ backgroundColor: '#00A5FF' }}
                        />
                      }
                      value={vehicleStats.escalados}
                      label='Escalados'
                    />
                    <EscalonamentoLegendItem
                      icon={
                        <LegendIcon
                          iconSrc={legendCancelIconSrc}
                          background={{
                            backgroundImage:
                              'linear-gradient(90.4903deg, #FF5C00 0.42425%, #EDBF67 99.576%)',
                          }}
                          iconTranslate={{ x: -0.5, y: 0 }}
                        />
                      }
                      value={vehicleStats.naoEscalados}
                      label='Não Escalados'
                    />
                    <EscalonamentoLegendItem
                      icon={
                        <LegendIcon
                          iconSrc={legendCancelIconSrc}
                          background={{ backgroundColor: '#8695AF' }}
                          iconTranslate={{ x: 0.5, y: 0.5 }}
                        />
                      }
                      value={vehicleStats.emOficina}
                      label='Em Oficina'
                    />
                  </Box>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {vehicleStats.alerts.map((item, index) => (
                  <Box
                    key={item.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      height: '48px',
                      borderBottom:
                        index === vehicleStats.alerts.length - 1
                          ? 'none'
                          : '1px solid rgba(134, 149, 175, 0.15)',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', px: '4px' }}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '100px',
                          backgroundColor: '#FF5C00',
                          color: '#FFFFFF',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontFamily: 'Poppins',
                          fontWeight: 600,
                          fontSize: '16px',
                          lineHeight: 1.75,
                        }}
                      >
                        {item.count}
                      </Box>
                    </Box>
                    <Typography
                      sx={{
                        flex: 1,
                        fontFamily: 'Poppins',
                        fontWeight: 500,
                        fontSize: '13px',
                        color: '#092056',
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <ChevronRightRoundedIcon
                        sx={{ color: '#00A5FF', fontSize: 20 }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            backgroundColor: '#FFFFFF',
            borderRadius: '10px',
            padding: '24px 16px',
            boxShadow: '0px 8px 20px rgba(9, 32, 86, 0.08)',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            '@keyframes statusRowIn': {
              '0%': {
                opacity: 0,
                transform: 'translateY(6px)',
              },
              '100%': {
                opacity: 1,
                transform: 'translateY(0)',
              },
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            <Tabs
              value={alertsTab}
              onChange={(_, value) => setAlertsTab(value)}
              TabIndicatorProps={{
                style: {
                  backgroundColor: '#00A5FF',
                  height: '2px',
                  borderRadius: '2px',
                },
              }}
              sx={{
                minHeight: '32px',
                '& .MuiTabs-flexContainer': {
                  justifyContent: 'center',
                },
                '& .MuiTab-root': {
                  minHeight: '32px',
                  fontFamily: 'Poppins',
                  fontWeight: 600,
                  fontSize: '13px',
                  textTransform: 'none',
                  color: '#8695AF',
                  padding: '6px 12px',
                  '&.Mui-selected': {
                    color: '#00A5FF',
                  },
                },
              }}
            >
              <Tab label='Alertas' />
              <Tab label='Últimas Ações' />
            </Tabs>
            {alertsTab === 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '8px',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Poppins',
                    fontWeight: 600,
                    fontSize: '16px',
                    lineHeight: 1.75,
                    letterSpacing: '0.15px',
                    color: '#092056',
                  }}
                >
                  Não Lidos
                </Typography>
                <CustomButton
                  variant='text'
                  size='small'
                  sx={{
                    color: '#00A5FF',
                    fontWeight: 600,
                    fontSize: '14px',
                    lineHeight: '26px',
                    textTransform: 'none',
                    padding: 0,
                  }}
                >
                  Marcar como Lidos
                </CustomButton>
              </Box>
            )}
          </Box>

          {alertsTab === 0 ? (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {data.alerts.unread.map((alert, index) => (
                  <Box
                    key={alert.id}
                    sx={{
                      display: 'flex',
                      gap: '8px',
                      padding: '8px',
                      borderRadius: '5px',
                      backgroundColor: '#E3F2FD',
                      animation: 'statusRowIn 240ms ease',
                      animationDelay: `${index * 40}ms`,
                      animationFillMode: 'both',
                      willChange: 'transform, opacity',
                    }}
                  >
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CheckRoundedIcon sx={{ color: '#00A5FF', fontSize: 18 }} />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <Typography
                        sx={{
                          fontFamily: 'Poppins',
                          fontWeight: 600,
                          fontSize: '14px',
                          lineHeight: 1.5,
                          letterSpacing: '0.15px',
                          color: '#092056',
                        }}
                      >
                        {renderAlertTitle(alert.title)}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: 'Poppins',
                          fontSize: '11px',
                          lineHeight: 1.3,
                          color: '#8695AF',
                        }}
                      >
                        {alert.timestamp}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              <Typography
                sx={{
                  fontFamily: 'Poppins',
                  fontWeight: 600,
                  fontSize: '16px',
                  lineHeight: 1.75,
                  letterSpacing: '0.15px',
                  color: '#092056',
                  marginTop: '8px',
                }}
              >
                Lidos
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {data.alerts.read.map((alert, index) => (
                  <Box
                    key={alert.id}
                    sx={{
                      display: 'flex',
                      gap: '8px',
                      padding: '8px',
                      borderRadius: '5px',
                      border: '1px solid rgba(134, 149, 175, 0.15)',
                      animation: 'statusRowIn 240ms ease',
                      animationDelay: `${(index + data.alerts.unread.length) * 40}ms`,
                      animationFillMode: 'both',
                      willChange: 'transform, opacity',
                    }}
                  >
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CheckRoundedIcon sx={{ color: '#93A2BD', fontSize: 18 }} />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <Typography
                        sx={{
                          fontFamily: 'Poppins',
                          fontWeight: 600,
                          fontSize: '14px',
                          lineHeight: 1.5,
                          letterSpacing: '0.15px',
                          color: '#092056',
                        }}
                      >
                        {renderAlertTitle(alert.title)}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: 'Poppins',
                          fontSize: '11px',
                          lineHeight: 1.3,
                          color: '#8695AF',
                        }}
                      >
                        {alert.timestamp}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {lastActions.map((action, index) => (
                <Box
                  key={action.id}
                  sx={{
                    display: 'flex',
                    gap: '10px',
                    alignItems: 'center',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    backgroundColor: '#EAF6FF',
                    animation: 'statusRowIn 240ms ease',
                    animationDelay: `${index * 40}ms`,
                    animationFillMode: 'both',
                    willChange: 'transform, opacity',
                  }}
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <ReplyRoundedIcon sx={{ color: '#00A5FF', fontSize: 18 }} />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <Typography
                      sx={{
                        fontFamily: 'Poppins',
                        fontWeight: 600,
                        fontSize: '14px',
                        lineHeight: 1.4,
                        letterSpacing: '0.15px',
                        color: '#092056',
                      }}
                    >
                      {renderAlertTitle(action.title)}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'Poppins',
                        fontSize: '11px',
                        lineHeight: 1.3,
                        color: '#8695AF',
                      }}
                    >
                      {action.timestamp}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
});

export default SchedulesStatusDashboard;
